import type { PickupLocation } from '@otw/models'
import type { ContactCompany } from '@otw/models/leads/contactCompanies'
import type { Lead } from '@otw/models/leads/lead'
import {
  getLeadDropoffLocation,
  isPickupLocationAddressComplete,
} from '@otw/models/leads/leadShippingValidation'
import { normalizeLeadTruckDropoffLocationIds } from '@otw/models/leads/lead'

export { getLeadDropoffLocation, isPickupLocationAddressComplete }

export const getTruckDropoffLocationId = (
  lead: Lead,
  truckId: string,
): string | undefined => {
  const map = normalizeLeadTruckDropoffLocationIds(lead.truckDropoffLocationIds)
  return map[truckId]?.trim() || undefined
}

export const getClosedLeadDropoffLocationId = (
  lead: Lead,
  vehicleId: string,
): string | undefined => getTruckDropoffLocationId(lead, vehicleId)

function parseLeadUpdatedAtMs(lead: Lead): number {
  const value = lead.updatedAt
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'string') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime()
  }
  return 0
}

/** Most recently updated closed lead that includes the given inventory vehicle id. */
export const findClosedLeadForVehicle = (
  leads: Lead[] | undefined,
  vehicleId: string,
): Lead | null => {
  if (!leads?.length || !vehicleId.trim()) return null

  const matching = leads.filter(
    (lead) =>
      lead.status === 'closed' && (lead.truckIds ?? []).includes(vehicleId),
  )
  if (matching.length === 0) return null

  return matching.sort((a, b) => parseLeadUpdatedAtMs(b) - parseLeadUpdatedAtMs(a))[0]
}

const isNonLostLeadLinkedToVehicle = (lead: Lead, vehicleId: string): boolean =>
  lead.status !== 'lost' && (lead.truckIds ?? []).includes(vehicleId)

/**
 * Most recently updated non-lost lead that includes the truck and has a dropoff
 * location selected for it.
 */
export const findLeadForPendingSale = (
  leads: Lead[] | undefined,
  vehicleId: string,
): Lead | null => {
  if (!leads?.length || !vehicleId.trim()) return null

  const matching = leads.filter(
    (lead) =>
      isNonLostLeadLinkedToVehicle(lead, vehicleId) &&
      Boolean(getTruckDropoffLocationId(lead, vehicleId)),
  )
  if (matching.length === 0) return null

  return matching.sort((a, b) => parseLeadUpdatedAtMs(b) - parseLeadUpdatedAtMs(a))[0]
}

/**
 * Returns an error message when the vehicle cannot be marked Pending Sale,
 * or null when the lead + dropoff requirements are met.
 */
export const getPendingSaleLeadRequirementError = (
  leads: Lead[] | undefined,
  vehicleId: string,
): string | null => {
  if (!vehicleId.trim()) {
    return 'This truck must be linked to a lead before it can be marked Pending Sale.'
  }

  if (findLeadForPendingSale(leads, vehicleId)) {
    return null
  }

  const linkedNonLost = (leads ?? []).some((lead) =>
    isNonLostLeadLinkedToVehicle(lead, vehicleId),
  )

  if (!linkedNonLost) {
    return 'This truck must be linked to a lead before it can be marked Pending Sale.'
  }

  return 'This truck must have a dropoff location selected on its lead before it can be marked Pending Sale.'
}

export const getLeadTruckDropoffStatus = (
  lead: Lead,
): { assigned: number; total: number } => {
  const truckIds = lead.truckIds ?? []
  const map = normalizeLeadTruckDropoffLocationIds(lead.truckDropoffLocationIds)
  const assigned = truckIds.filter((id) => Boolean(map[id]?.trim())).length
  return { assigned, total: truckIds.length }
}

const isTruckDropoffValidForCompany = (
  dropoffLocationId: string,
  company: ContactCompany | undefined,
): boolean => {
  if (!company) return true
  const allowedIds = new Set(company.dropoffLocationIds ?? [])
  return allowedIds.has(dropoffLocationId)
}

/** True when a lead may be moved to the closed column / status. */
export const canMoveLeadToClosed = (
  lead: Lead,
  pickupLocations: PickupLocation[] | undefined,
  contactCompanies?: ContactCompany[] | undefined,
): boolean => {
  const truckIds = lead.truckIds ?? []
  if (truckIds.length === 0) return true

  const truckDropoffs = normalizeLeadTruckDropoffLocationIds(
    lead.truckDropoffLocationIds,
  )
  const company = lead.companyId?.trim()
    ? contactCompanies?.find((c) => c.id === lead.companyId)
    : undefined

  return truckIds.every((truckId) => {
    const dropoffId = truckDropoffs[truckId]?.trim()
    if (!dropoffId) return false
    if (!isTruckDropoffValidForCompany(dropoffId, company)) return false
    const location = pickupLocations?.find((loc) => loc.id === dropoffId)
    return isPickupLocationAddressComplete(location)
  })
}

export const CLOSED_DROPFF_REQUIREMENT_MESSAGE =
  'Each linked truck must have a complete dropoff address (street, city, state, and ZIP) from the company\'s dropoff locations before this lead can be closed.'
